import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import axios, { CancelToken } from "axios";

import { useAppDispatch, useAppSelector } from "hooks/redux";
import { loadLeague, loadFacility } from "redux/actions/customer/leagueRegistration";
import { GetGuestLeagueSponsorship, IGuestSponsorship } from "api/rpc/2024-04/guest/league";
import { StatusCode } from "api/protocols";
import LeagueHeader from "../LeagueHeader/LeagueHeader";
import EventPublicHome from "pages/guest/eventHome/EventPublicHome";
import { determineLeagueRegistrationWarning } from "../LeagueRegistration/LeagueRegister";

interface IParams {
  facilityShortName: string;
  leagueHandle: string;
}

export default function LeagueHome() {
  const { facilityShortName, leagueHandle } = useParams<IParams>();
  const dispatch = useAppDispatch();
  const { leagueRegistrationStore } = useAppSelector(store => store);
  const [sponsors, setSponsors] = useState<IGuestSponsorship[]>([]);

  useEffect(() => {
    if (!leagueRegistrationStore.facility) {
      void dispatch(loadFacility(facilityShortName, true));
    }
  }, []);

  useEffect(() => {
    if (leagueRegistrationStore.facility && !leagueRegistrationStore.league) {
      void dispatch(loadLeague(leagueRegistrationStore.facility.id, leagueHandle, true));
    }
  }, [leagueRegistrationStore.facility]);

  useEffect(() => {
    if (!leagueRegistrationStore.league) {
      return;
    }

    const source = axios.CancelToken.source();
    void loadSponsors(source.token);
    return () => source.cancel();
  }, [leagueRegistrationStore.league]);

  async function loadSponsors(token?: CancelToken) {
    const response = await GetGuestLeagueSponsorship(
      { league_id: leagueRegistrationStore.league.id },
      false,
      token,
    );

    if (token && token.reason) {
      return;
    }
    if (response.status === StatusCode.OK) {
      setSponsors(response.data ?? []);
    }
  }

  const league = leagueRegistrationStore.league;
  const facility = leagueRegistrationStore.facility;

  return (
    <>
      <LeagueHeader facility={facility} league={league} />
      {facility && league && (
        <EventPublicHome
          name={league.name}
          description={league.description}
          facility={facility}
          participantCount={league.total_participant_count}
          registerPath={`/league/${facilityShortName}/${leagueHandle}/register`}
          sponsors={sponsors}
          calendarUid={`league-${league.id}@tee-on`}
          startDate={league.start_date}
          endDate={league.end_date}
          startTime={league.start_time}
          weekDay={league.week_day}
          registrationAvailability={determineLeagueRegistrationWarning(league)}
          registrationOpenDate={league.registration_open_date}
          registrationCloseDate={league.registration_close_date}
        />
      )}
    </>
  );
}
